import { Eye, Shuffle, Users, Footprints, TrendingUp } from "lucide-react";
import { RABBIT_PRINCIPLES, STORY_IMAGES } from "../data/brochure";

const ICONS = { aware: Eye, adapt: Shuffle, connect: Users, move: Footprints, grow: TrendingUp };

const StoryCard = ({ image, title, subtitle, text, testId }) => (
  <div className="story-card" data-testid={testId}>
    <img src={image} alt={title} className="story-card-image" loading="lazy" />
    <div className="story-card-body">
      <h3 className="story-card-title">{title}</h3>
      <span className="story-card-subtitle">{subtitle}</span>
      <p>{text}</p>
    </div>
  </div>
);

const Principles = () => (
  <div className="principles" data-testid="rabbit-principles">
    <div className="principles-head">
      <h3 className="principles-title">The <span>Rabbit Principles</span></h3>
      <span className="principles-kicker">What the rabbit teaches us</span>
    </div>
    <div className="principles-grid">
      {RABBIT_PRINCIPLES.map(({ key, title, lines }, i) => {
        const Icon = ICONS[key];
        return (
          <div className="principle" key={key} style={{ animationDelay: `${i * 90}ms` }} data-testid={`principle-${key}`}>
            <div className="principle-icon"><Icon /></div>
            <h4>{title}</h4>
            {lines.map((l) => <p key={l}>{l}</p>)}
          </div>
        );
      })}
    </div>
  </div>
);

export const RabbitStory = ({ innerRef }) => (
  <section id="story" className="story-section section-animate" ref={innerRef} data-testid="story-section">
    <div className="story-hero">
      <div className="story-text">
        <span className="story-eyebrow">Our Story &nbsp;|&nbsp; Our Philosophy &nbsp;|&nbsp; Our Purpose</span>
        <h2 className="story-headline" data-testid="story-title">Beyond the <span>Rabbit's Foot</span></h2>
        <p className="story-lead">Don't chase luck. Build it.</p>
        <p>At Fit Farm, we believe growth isn't about luck. It's about people, the right environment, and the courage to keep moving forward.</p>
        <p>The rabbit has long been known as a symbol of good luck. But for us, it represents something much deeper — awareness, adaptability, resilience and the relentless spirit to grow. It's not about what you're born with, but what you choose to become.</p>
      </div>
      <div className="story-mascot">
        <img src={STORY_IMAGES.mascot} alt="Fit Farm rabbit mascot" data-testid="story-mascot" />
      </div>
    </div>

    <div className="story-cards">
      <StoryCard testId="story-card-rabbit" image={STORY_IMAGES.rabbit} title="The Rabbit" subtitle="The spirit to move forward"
        text="Rabbits are alert, curious and adaptable. They notice change, respond quickly and keep moving — qualities we see in great individuals and teams. The rabbit reminds us to stay aware, be adaptable, connect with others, take action and keep growing." />
      <StoryCard testId="story-card-farm" image={STORY_IMAGES.farm} title="The Farm" subtitle="The environment for growth"
        text="Every farm is a place of potential. It provides the right environment to nurture, challenge and develop. People are no different. At Fit Farm, we create experiences where individuals and teams can experiment, connect, reflect and unlock what they're truly capable of." />
    </div>

    <Principles />

    <div className="story-closing" data-testid="story-closing">
      <p className="story-closing-script">A brighter tomorrow, built together.</p>
      <p>At Fit Farm, we don't just train people. We create meaningful experiences that move people — mentally, emotionally and professionally. Because when people grow, so do teams, organisations and communities.</p>
      <span className="story-closing-tag">People. Performance. Progress.</span>
    </div>
  </section>
);
